import React from 'react';
import { ClipboardList, Ruler, Wrench, HardHat, CheckCircle2, Headphones } from 'lucide-react';
import { turnkeyProcess } from '../data/mock';

const TurnkeyProcess = () => {
  const iconMap = {
    ClipboardList: ClipboardList,
    Ruler: Ruler,
    Wrench: Wrench,
    HardHat: HardHat,
    CheckCircle2: CheckCircle2,
    Headphones: Headphones
  };

  return (
    <section id="process" className="py-20 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5">
        <img
          src="https://images.unsplash.com/photo-1581090694255-723d2dee30bc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxlbmdpbmVlcmluZyUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3NzAxMzM1NjZ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          loading="lazy"
          width="1200"
          height="1600"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-blue-900">Turnkey Process</span>
          </h2>
          <div className="w-24 h-1 bg-blue-900 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            End-to-end project execution from initial consultation to post-installation support, ensuring seamless delivery of lighting solutions
          </p>
        </div>

        {/* Process Timeline */}
        <div className="max-w-5xl mx-auto">
          {turnkeyProcess.map((step, index) => {
            const Icon = iconMap[step.icon];
            const isEven = index % 2 === 0;

            return (
              <div key={step.step} className="relative mb-12 last:mb-0">
                {/* Connector Line - hide for last item */}
                {index < turnkeyProcess.length - 1 && (
                  <div className="hidden md:block absolute left-1/2 top-20 w-0.5 h-full bg-blue-200 -translate-x-1/2"></div>
                )}

                <div className={`flex flex-col md:flex-row items-center gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Content Card */}
                  <div className="flex-1 w-full">
                    <div className={`bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-500 border border-white hover:border-blue-100 hover:-translate-y-2 ${isEven ? 'md:text-right' : 'md:text-left'} relative overflow-hidden group`}>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-transparent rounded-bl-full opacity-50 group-hover:scale-110 transition-transform duration-500 z-0"></div>
                      <div className="relative z-10">
                        <div className={`inline-flex items-center gap-2 bg-blue-50 border border-blue-100/50 text-blue-900 px-4 py-1.5 rounded-full text-sm font-bold mb-4 shadow-sm`}>
                          Step {step.step}
                        </div>
                        <h3 className="text-2xl font-extrabold text-gray-900 mb-3 group-hover:text-blue-900 transition-colors">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed font-medium">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Icon Circle */}
                  <div className="relative flex-shrink-0">
                    <div className="w-20 h-20 bg-blue-900 rounded-full flex items-center justify-center shadow-lg z-10 relative">
                      <Icon className="text-white" size={32} />
                    </div>
                    <div className="absolute inset-0 bg-blue-300 rounded-full animate-ping opacity-20"></div>
                  </div>

                  {/* Spacer for alignment */}
                  <div className="flex-1 hidden md:block"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Feature Highlights */}
        <div className="mt-24 grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {[
            { value: '100%', label: 'Project Completion Rate' },
            { value: '15+', label: 'Years of Expertise' },
            { value: '800+', label: 'Successful Projects' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white/80 backdrop-blur-md border border-blue-50 rounded-3xl p-8 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_40px_rgb(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-2 group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-900 to-blue-600 mb-3 transition-transform duration-300 inline-block drop-shadow-sm">{stat.value}</div>
                <div className="text-gray-600 font-bold uppercase tracking-wider text-sm">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TurnkeyProcess;
